import {
    Box,
    Grid,
    Input,
    Radio,
    RadioGroup,
    Stack,
    Text
} from "@chakra-ui/react";
import ManagedCheckBox from "../../form/ManagedCheckBox";
import {ChangeEvent, FocusEvent, KeyboardEvent, useContext, useEffect, useRef, useState} from "react";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {Spot, SpotPlayMode} from "../../types";

type Props = {
    spot: Spot
}

const SpotListGeneral = ({spot}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const isOnDemandMode = appContext.isOnDemand();

    const [playMode, setPlayMode] = useState<SpotPlayMode>(spot?.playMode ? spot.playMode : "stop");
    const [playFullAfterTime, setPlayFullAfterTime] = useState<number>(spot?.playFullAfter ? spot.playFullAfter : 0);
    const [loop, setLoop] = useState<boolean>(spot?.loop ? spot.loop : false);
    const [runOnes, setRunOnes] = useState<boolean>(spot?.runOnes ? spot.runOnes : false);
    const [notes, setNotes] = useState<string>(spot?.note || "");
    const isSyncingValues = useRef<boolean>(false);

    const currentSelection = appContext.getSelection();

    useEffect(() => {
        const nextPlayMode = spot?.playMode ? spot.playMode : "stop";
        const nextPlayFullAfter = spot?.playFullAfter ? spot.playFullAfter : 0;
        const nextLoop = spot?.loop ? spot.loop : false;
        const nextRunOnes = spot?.runOnes ? spot.runOnes : false;
        isSyncingValues.current = true;
        setPlayMode(nextPlayMode);
        setPlayFullAfterTime(nextPlayFullAfter);
        setLoop(nextLoop);
        setRunOnes(nextRunOnes);
    }, [spot.id, spot.playMode, spot.playFullAfter, spot.loop, spot.runOnes]);

    useEffect(() => {
        if (isSyncingValues.current) {
            isSyncingValues.current = false;
            return;
        }
        if (currentSelection &&
            (
                playMode !== spot.playMode ||
                playFullAfterTime !== spot.playFullAfter ||
                loop !== spot.loop ||
                runOnes !== spot.runOnes
            )
        ) {
            appContext.updateSpot(spot, {
                ...spot,
                playMode: playMode,
                playFullAfter: playFullAfterTime,
                loop: loop,
                runOnes: runOnes
            } as Spot)
        }
    }, [appContext, currentSelection, loop, playFullAfterTime, playMode, runOnes, spot]);

    useEffect(() => {
        setNotes(spot?.note || "");
    }, [spot.id, spot.note]);


    const getIntValue = (event: ChangeEvent<HTMLInputElement>): number => {
        const value = event.target.value;
        if (value.length === 0) {
            return 0;
        } else {
            const intVal = parseInt(value);
            return intVal >= 0 ? intVal : 0;
        }
    }

    const setPlayModeString = (next: string) => {
        appContext.isDraft() && setPlayMode(next as SpotPlayMode);
    }

    return <Stack spacing={"8px"}>
        <Box>
            {!isOnDemandMode ? (
                <ManagedCheckBox name={"loop"} defaultValue={loop} onChange={setLoop}
                                 disabled={!appContext.isDraft()}>
                    Loop sounds <Text fontWeight={"light"} display={"inline-block"}>(while this spot is
                    active -
                    see &quot;When exiting the spot&quot;)</Text>
                </ManagedCheckBox>
            ) : null}

            <Box></Box>

            <ManagedCheckBox name={"runOnes"} defaultValue={runOnes} onChange={setRunOnes}
                             disabled={!appContext.isDraft()}>
                Only allow this spot to be experienced ones
            </ManagedCheckBox>
        </Box>

        {!isOnDemandMode ? (
            <>
                <Text fontWeight={"semibold"}>
                    When exiting spot
                </Text>
                <Box>
                    <RadioGroup onChange={setPlayModeString}
                                value={playMode}
                                marginBottom={"24px"}
                                isDisabled={!appContext.isDraft()}>
                        <Stack direction={"column"}>
                            <Radio value={"stop"}>Stop all sounds <Text fontWeight={"light"}
                                                                        display={"inline-block"}>(start
                                from the beginning if the spot is entered again)</Text></Radio>
                            <Radio value={"pause"}>Pause current sound <Text fontWeight={"light"}
                                                                             display={"inline-block"}>(start
                                the
                                same
                                place in current sound if the spot is entered again)</Text></Radio>
                            <Radio value={"play"}>
                                Keep playing all sounds <Text fontWeight={"light"} display={"inline-block"}>(playback
                                continues even if the spot is left)</Text>
                            </Radio>
                            {
                                playMode === "play" &&
                                <Grid gridTemplateColumns={"max-content max-content max-content"}
                                      columnGap={"8px"}
                                      marginTop={"8px"} paddingLeft={"80px"}>
                                    <Text>after being in the spot for</Text>
                                    <Input size={"sm"}
                                           width={"60px"}
                                           onFocus={(event: FocusEvent<HTMLInputElement>) => {
                                               event.target.select();
                                           }}
                                           defaultValue={"" + playFullAfterTime}
                                           onBlur={(event: ChangeEvent<HTMLInputElement>) => setPlayFullAfterTime(getIntValue(event))}
                                           onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                               if (event.key === "Enter") {
                                                   const value = event.currentTarget.value;
                                                   setPlayFullAfterTime(value ? parseInt(value) || 0 : 0);
                                               }
                                           }}
                                           isDisabled={!appContext.isDraft()}/>
                                    <Text>seconds</Text>
                                </Grid>
                            }
                        </Stack>
                    </RadioGroup>
                </Box>
            </>
        ) : null}
        <Box>
            <Text>Notes</Text>
            <Input
                size={"sm"}
                placeholder={"Optional notes"}
                value={notes}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setNotes(event.target.value)}
                onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                    if (!appContext.isDraft()) {
                        return;
                    }
                    const trimmed = event.target.value.trim();
                    const currentNote = spot.note || "";
                    if (trimmed === currentNote) {
                        setNotes(currentNote);
                        return;
                    }
                    appContext.updateSpot(spot, {
                        ...spot,
                        note: trimmed.length > 0 ? trimmed : undefined
                    } as Spot);
                    setNotes(trimmed);
                }}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === "Enter") {
                        event.currentTarget.blur();
                    }
                }}
                disabled={!appContext.isDraft()}
            />
        </Box>
    </Stack>
}

export default SpotListGeneral
